import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  confirmPaymentIntent,
  createPaymentIntent,
  getPayment,
  listPayments,
  refundPayment,
} from '../services/payments.service';

export const paymentKeys = {
  all: ['payments'] as const,
  list: (params?: object) => [...paymentKeys.all, 'list', params] as const,
  detail: (id: string) => [...paymentKeys.all, 'detail', id] as const,
};

export function usePayments(params?: { skip?: number; take?: number; status?: string }) {
  return useQuery({
    queryKey: paymentKeys.list(params),
    queryFn: () => listPayments(params),
  });
}

export function usePayment(id: string) {
  return useQuery({
    queryKey: paymentKeys.detail(id),
    queryFn: () => getPayment(id),
    enabled: Boolean(id),
  });
}

export function useCreatePaymentIntent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      subscriptionId,
      idempotencyKey,
    }: {
      subscriptionId: string;
      idempotencyKey: string;
    }) => createPaymentIntent({ subscriptionId }, idempotencyKey),
    onSuccess: (data) => {
      qc.setQueryData(paymentKeys.detail(data.id), data);
    },
  });
}

export function useConfirmPaymentIntent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (paymentId: string) => confirmPaymentIntent(paymentId),
    onSuccess: (data) => {
      qc.setQueryData(paymentKeys.detail(data.id), data);
      void qc.invalidateQueries({ queryKey: paymentKeys.all });
    },
  });
}

export function useRefundPayment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, amount, reason }: { id: string; amount?: number; reason: string }) =>
      refundPayment(
        id,
        { ...(amount !== undefined ? { amount } : {}), reason },
        crypto.randomUUID(),
      ),
    onSuccess: (_, { id }) => {
      void qc.invalidateQueries({ queryKey: paymentKeys.detail(id) });
      void qc.invalidateQueries({ queryKey: paymentKeys.all });
    },
  });
}
